/**
 * Persist legacy recording Blobs in IndexedDB so a refresh / hot-reload
 * does not wipe an hour of footage. Cleared after successful submit,
 * explicit reset, or TTL expiry (unused drafts).
 *
 * Clips are keyed by question text (stable) with a legacy index fallback
 * so older drafts still restore after the duplicate-answer bug.
 */

import { questionDedupeKey, dedupeQuestionResponses } from './questionResponses';

const DB_NAME = 'sizzle-legacies-recordings';
const DB_VERSION = 1;
const STORE_CLIPS = 'clips';
const DRAFT_KEY = 'recordingDraftId';
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // unused drafts expire after 7 days

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_CLIPS)) {
        const store = db.createObjectStore(STORE_CLIPS, { keyPath: 'id' });
        store.createIndex('draftId', 'draftId', { unique: false });
        store.createIndex('updatedAt', 'updatedAt', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('Failed to open recording cache'));
  });
}

function txDone(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('IndexedDB transaction aborted'));
  });
}

function stableQuestionKey(questionText) {
  const key = questionDedupeKey(questionText);
  // Keep IDB key path-safe and bounded
  return encodeURIComponent(key).slice(0, 180) || 'unknown';
}

function clipIdForQuestion(draftId, questionText, kind) {
  return `${draftId}:qk:${stableQuestionKey(questionText)}:${kind}`;
}

export function getRecordingDraftId() {
  try {
    let id = sessionStorage.getItem(DRAFT_KEY);
    if (!id) {
      id = `draft-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(DRAFT_KEY, id);
    }
    return id;
  } catch (_) {
    return `draft-${Date.now()}`;
  }
}

export function peekRecordingDraftId() {
  try {
    return sessionStorage.getItem(DRAFT_KEY);
  } catch (_) {
    return null;
  }
}

export function rotateRecordingDraftId() {
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch (_) {}
  return getRecordingDraftId();
}

async function putRecord(record) {
  const db = await openDb();
  const tx = db.transaction(STORE_CLIPS, 'readwrite');
  tx.objectStore(STORE_CLIPS).put(record);
  await txDone(tx);
  db.close();
}

async function deleteById(id) {
  const db = await openDb();
  const tx = db.transaction(STORE_CLIPS, 'readwrite');
  tx.objectStore(STORE_CLIPS).delete(id);
  await txDone(tx);
  db.close();
}

async function getAllForDraft(draftId) {
  const db = await openDb();
  const tx = db.transaction(STORE_CLIPS, 'readonly');
  const index = tx.objectStore(STORE_CLIPS).index('draftId');
  const req = index.getAll(draftId);
  const rows = await new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
  await txDone(tx);
  db.close();
  return rows;
}

/**
 * Write / update a single question media blob. Pass null blob to remove.
 */
export async function saveQuestionClip(draftId, questionText, kind, blob, questionIndex = 0) {
  if (!draftId || !kind) return;
  const id = clipIdForQuestion(draftId, questionText, kind);
  if (!(blob instanceof Blob) || blob.size < 1) {
    try { await deleteById(id); } catch (_) {}
    return;
  }
  await putRecord({
    id,
    draftId,
    questionKey: questionDedupeKey(questionText),
    questionIndex: Number(questionIndex) || 0,
    kind, // 'video' | 'audio' | 'cover'
    blob,
    mimeType: blob.type || '',
    size: blob.size,
    updatedAt: Date.now(),
  });
}

/**
 * Sync all question media + optional cover photo for the current draft.
 * Removes cache entries that no longer exist in memory.
 */
export async function syncRecordingCache(draftId, questions = [], coverPhoto = null) {
  if (!draftId) return;
  const desired = new Set();
  const clean = dedupeQuestionResponses(questions);

  const hasAnyMedia = clean.some(
    (q) =>
      (q?.videoBlob instanceof Blob && q.videoBlob.size > 0) ||
      (q?.audioBlob instanceof Blob && q.audioBlob.size > 0)
  ) || (coverPhoto instanceof Blob && coverPhoto.size > 0);

  // Never wipe the disk cache when in-memory questions are empty/skipped —
  // that happens if the user revisits an earlier step and would destroy
  // recoverable recordings.
  if (!hasAnyMedia) {
    console.warn('💾 Skipping recording-cache sync — no in-memory media (keeping existing cache)');
    return;
  }

  for (let i = 0; i < clean.length; i++) {
    const q = clean[i];
    if (q?.skipped && !(q.videoBlob instanceof Blob) && !(q.audioBlob instanceof Blob)) continue;
    if (q?.videoBlob instanceof Blob && q.videoBlob.size > 0) {
      const id = clipIdForQuestion(draftId, q.question, 'video');
      desired.add(id);
      await saveQuestionClip(draftId, q.question, 'video', q.videoBlob, i);
    }
    if (q?.audioBlob instanceof Blob && q.audioBlob.size > 0) {
      const id = clipIdForQuestion(draftId, q.question, 'audio');
      desired.add(id);
      await saveQuestionClip(draftId, q.question, 'audio', q.audioBlob, i);
    }
  }

  if (coverPhoto instanceof Blob && coverPhoto.size > 0) {
    const id = clipIdForQuestion(draftId, '__cover__', 'cover');
    desired.add(id);
    await saveQuestionClip(draftId, '__cover__', 'cover', coverPhoto, 0);
  }

  const existing = await getAllForDraft(draftId);
  for (const row of existing) {
    if (!desired.has(row.id)) {
      try { await deleteById(row.id); } catch (_) {}
    }
  }
}

function applyBlobToQuestion(question, kind, blob) {
  const next = { ...question };
  // A restored recording means this question was answered — never keep skipped
  next.skipped = false;
  if (kind === 'video') {
    next.videoBlob = blob;
    next.audioBlob = undefined;
    next.response = blob;
    next.responseType = 'video';
  } else if (kind === 'audio') {
    next.audioBlob = blob;
    next.videoBlob = undefined;
    next.response = blob;
    next.responseType = 'audio';
  }
  return next;
}

function questionHasBlob(q) {
  return (q?.videoBlob instanceof Blob && q.videoBlob.size > 0)
    || (q?.audioBlob instanceof Blob && q.audioBlob.size > 0);
}

/**
 * Rehydrate Blobs onto questions loaded from sessionStorage.
 * Returns { questions, coverPhoto, restoredCount } (questions are deduped).
 */
export async function restoreRecordingCache(draftId, questions = [], coverPhoto = null) {
  if (!draftId) {
    return { questions: dedupeQuestionResponses(questions), coverPhoto, restoredCount: 0 };
  }

  let rows = [];
  try {
    rows = await getAllForDraft(draftId);
  } catch (err) {
    console.warn('Could not read recording cache:', err);
    return { questions: dedupeQuestionResponses(questions), coverPhoto, restoredCount: 0 };
  }

  let restoredCount = 0;
  let nextQuestions = (questions || []).map((q) => ({ ...q }));

  if (rows.length) {
    const byQuestionKind = new Map();
    const byIndexKind = new Map();
    const unused = [];

    for (const row of rows) {
      if (!(row.blob instanceof Blob) || row.blob.size < 1) continue;
      if (row.kind === 'cover') continue;
      if (row.questionKey) {
        byQuestionKind.set(`${row.questionKey}:${row.kind}`, row);
      }
      if (row.questionIndex != null) {
        byIndexKind.set(`${row.questionIndex}:${row.kind}`, row);
      }
      unused.push(row);
    }

    const used = new Set();

    // 1) Match by stable question text
    nextQuestions = nextQuestions.map((q) => {
      const key = questionDedupeKey(q.question);
      let next = q;
      for (const kind of ['video', 'audio']) {
        const row = byQuestionKind.get(`${key}:${kind}`);
        if (row?.blob instanceof Blob) {
          next = applyBlobToQuestion(next, kind, row.blob);
          used.add(row.id);
          restoredCount += 1;
        }
      }
      return next;
    });

    // 2) Legacy index match for rows not yet used
    nextQuestions = nextQuestions.map((q, i) => {
      if (questionHasBlob(q)) return q;
      let next = q;
      for (const kind of ['video', 'audio']) {
        const row = byIndexKind.get(`${i}:${kind}`);
        if (row?.blob instanceof Blob && !used.has(row.id)) {
          next = applyBlobToQuestion(next, kind, row.blob);
          used.add(row.id);
          restoredCount += 1;
        }
      }
      return next;
    });

    // 3) Leftover blobs → first questions still missing media (salvage duplicates)
    const leftover = unused
      .filter((r) => !used.has(r.id) && (r.kind === 'video' || r.kind === 'audio'))
      .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));

    for (const row of leftover) {
      const idx = nextQuestions.findIndex((q) => !q.skipped && !questionHasBlob(q)
        && (q.responseType === 'video' || q.responseType === 'audio' || !q.responseType));
      if (idx === -1) break;
      nextQuestions[idx] = applyBlobToQuestion(nextQuestions[idx], row.kind, row.blob);
      used.add(row.id);
      restoredCount += 1;
    }

    const coverRow = rows.find((r) => r.kind === 'cover' && r.blob instanceof Blob);
    if ((!coverPhoto || !(coverPhoto instanceof Blob)) && coverRow?.blob) {
      coverPhoto = coverRow.blob;
      restoredCount += 1;
    }
  }

  const deduped = dedupeQuestionResponses(nextQuestions);
  return { questions: deduped, coverPhoto, restoredCount };
}

/**
 * Delete every clip for a draft (after successful submit or start-over).
 */
export async function clearRecordingDraft(draftId) {
  if (!draftId) return;
  try {
    const rows = await getAllForDraft(draftId);
    for (const row of rows) {
      try { await deleteById(row.id); } catch (_) {}
    }
  } catch (err) {
    console.warn('Could not clear recording draft:', err);
  }
}

/**
 * Clear the active draft and forget its id (call after video is finished / reset).
 */
export async function clearActiveRecordingCache() {
  const draftId = peekRecordingDraftId();
  if (draftId) {
    await clearRecordingDraft(draftId);
  }
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch (_) {}
}

/**
 * Remove drafts older than TTL so abandoned recordings don't fill the disk.
 */
export async function purgeExpiredRecordingCache(ttlMs = TTL_MS) {
  try {
    const db = await openDb();
    const tx = db.transaction(STORE_CLIPS, 'readwrite');
    const store = tx.objectStore(STORE_CLIPS);
    const req = store.getAll();
    const rows = await new Promise((resolve, reject) => {
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
    const cutoff = Date.now() - ttlMs;
    for (const row of rows) {
      if (!row.updatedAt || row.updatedAt < cutoff) {
        store.delete(row.id);
      }
    }
    await txDone(tx);
    db.close();
  } catch (err) {
    console.warn('Could not purge expired recordings:', err);
  }
}
